import React, { useState } from 'react';
import { Layers, AlertCircle, CheckCircle2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { StatusBadge } from '@/components/common/StatusBadge';
import { cycleService } from '@/services/cycleService';
import { CycleStatus } from '@/types/database';

interface CycleStatusModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  cycleId: string;
  cycleCode: string;
  currentStatus: CycleStatus;
}

export const CycleStatusModal: React.FC<CycleStatusModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  cycleId,
  cycleCode,
  currentStatus,
}) => {
  const normalizedInitial = currentStatus === 'selling' ? 'active' : currentStatus;
  const [targetStatus, setTargetStatus] = useState<CycleStatus>(normalizedInitial);
  const [actualCloseDate, setActualCloseDate] = useState<string>(
    new Date().toISOString().split('T')[0]
  );
  const [loading, setLoading] = useState<boolean>(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const statuses: Array<{ status: CycleStatus; label: string; desc: string }> = [
    {
      status: 'active',
      label: 'Active',
      desc: 'Animals are on feed or sales are in progress. Standard operational phase.',
    },
    {
      status: 'settled',
      label: 'Settled',
      desc: 'Sales concluded and Grower Settlement liquidation statement finalized and frozen.',
    },
    {
      status: 'closed',
      label: 'Closed',
      desc: 'All physical, financial, and accounting activities finalized. Cycle is permanently archived.',
    },
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (targetStatus === currentStatus) {
      onClose();
      return;
    }

    setLoading(true);
    setErrorMsg(null);
    try {
      await cycleService.updateCycleStatus(
        cycleId,
        targetStatus,
        targetStatus === 'closed' ? actualCloseDate : null
      );
      onSuccess();
      onClose();
    } catch (err: any) {
      setErrorMsg(err.message || 'Failed to update cycle status.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-md p-4 sm:p-6">
        <DialogHeader className="border-b border-slate-200 pb-3">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-slate-900 text-white flex items-center justify-center font-bold">
              <Layers className="w-4 h-4" />
            </div>
            <div>
              <DialogTitle className="text-base sm:text-lg font-bold text-slate-900">
                Update Cycle Status
              </DialogTitle>
              <p className="text-xs text-slate-500 font-mono">Cycle: {cycleCode}</p>
            </div>
          </div>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 pt-2">
          {errorMsg && (
            <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-xs flex items-start gap-2">
              <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span>{errorMsg}</span>
            </div>
          )}

          <div className="space-y-2">
            <label className="block text-xs font-semibold text-slate-700">Current Status</label>
            <div>
              <StatusBadge status={currentStatus} category="cycle" />
            </div>
          </div>

          <div className="space-y-2">
            <label className="block text-xs font-semibold text-slate-700">Target Status</label>
            <div className="space-y-2">
              {statuses.map((s) => (
                <label
                  key={s.status}
                  className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                    targetStatus === s.status
                      ? 'border-emerald-600 bg-emerald-50/50 shadow-xs'
                      : 'border-slate-200 hover:bg-slate-50'
                  }`}
                >
                  <input
                    type="radio"
                    name="status"
                    value={s.status}
                    checked={targetStatus === s.status}
                    onChange={() => setTargetStatus(s.status)}
                    className="mt-1 text-emerald-600 focus:ring-emerald-500"
                  />
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-bold text-xs text-slate-900">{s.label}</span>
                      <StatusBadge status={s.status} category="cycle" showIcon={false} />
                    </div>
                    <p className="text-[11px] text-slate-500 mt-0.5 leading-relaxed">{s.desc}</p>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {targetStatus === 'closed' && (
            <div className="space-y-1.5 animate-in fade-in">
              <label className="block text-xs font-semibold text-slate-700">
                Actual Close Date <span className="text-red-500">*</span>
              </label>
              <Input
                type="date"
                required
                value={actualCloseDate}
                onChange={(e) => setActualCloseDate(e.target.value)}
                className="text-xs h-9"
              />
              <p className="text-[11px] text-slate-500">
                This date will be permanently recorded as the operational termination date of this cycle.
              </p>
            </div>
          )}

          <DialogFooter className="border-t border-slate-200 pt-4 flex items-center justify-end gap-2">
            <Button type="button" variant="outline" size="sm" onClick={onClose} disabled={loading}>
              Cancel
            </Button>
            <Button
              type="submit"
              size="sm"
              disabled={loading || targetStatus === currentStatus}
              className="bg-emerald-700 hover:bg-emerald-600 text-white font-semibold"
            >
              {loading ? 'Updating...' : 'Confirm Status Change'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
